import { App, Input } from 'antd';
import { useCallback } from 'react';

interface ReasonPromptOptions {
  title?: string;
  placeholder?: string;
  okText?: string;
  cancelText?: string;
  danger?: boolean;
  initialValue?: string;
  rows?: number;
}

/**
 * Replaces `window.prompt(...)` with an AntD Modal.confirm that contains a
 * TextArea. Resolves with the trimmed reason, or `null` if the user cancels.
 * Keeps the modal open and shows a warning if the user submits an empty value.
 */
export function useReasonPrompt() {
  const { modal, message } = App.useApp();

  return useCallback(
    (opts: ReasonPromptOptions = {}): Promise<string | null> => {
      return new Promise((resolve) => {
        let value = opts.initialValue ?? '';

        modal.confirm({
          title: opts.title ?? 'Reason required',
          icon: null,
          centered: true,
          okText: opts.okText ?? 'Confirm',
          cancelText: opts.cancelText ?? 'Cancel',
          okButtonProps: { danger: opts.danger },
          content: (
            <Input.TextArea
              rows={opts.rows ?? 3}
              autoFocus
              defaultValue={value}
              placeholder={opts.placeholder ?? 'Enter a reason'}
              onChange={(e) => {
                value = e.target.value;
              }}
              onPressEnter={(e) => {
                // Ctrl/Cmd+Enter submits; plain Enter inserts a newline.
                if (!(e.ctrlKey || e.metaKey)) return;
                e.preventDefault();
                (e.currentTarget.closest('.ant-modal')?.querySelector(
                  '.ant-btn-primary',
                ) as HTMLButtonElement | null)?.click();
              }}
            />
          ),
          onOk: () => {
            const trimmed = value.trim();
            if (!trimmed) {
              message.warning('Please enter a reason.');
              return Promise.reject();
            }
            resolve(trimmed);
          },
          onCancel: () => resolve(null),
        });
      });
    },
    [modal, message],
  );
}
